import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('live_updates.disabled_banner')

const ILLUSTRATION_WIDTH = '120px'
const ILLUSTRATION_HEIGHT = '88px'
const ILLUSTRATION_RADIUS = '12px'
const BADGE_SIZE = '22px'
const BADGE_OFFSET = '7px'
const BADGE_ICON_SIZE = '10px'
const CARD_WIDTH = '88px'

type Props = {
  testId?: string
}

// Decorative, so it carries no accessible text.
const Illustration = ({
  testId = 'live-updates-disabled-illustration',
}: Props) => {
  const [css] = useStyletron()
  const line = (key: string) => (
    <i
      key={key}
      className={css({
        display: 'block',
        flex: 1,
        height: '1px',
        backgroundColor: palette.phoneRailLine,
      })}
    />
  )
  const dot = (key: string, isOn: boolean) => (
    <i
      key={key}
      className={css({
        display: 'block',
        flexShrink: 0,
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: isOn
          ? palette.phoneRailDotOn
          : palette.phoneRailDotOff,
      })}
    />
  )
  const label = (text: string) => (
    <span
      key={text}
      className={css({
        fontSize: '5px',
        lineHeight: '1',
        color: palette.phoneTextMuted,
      })}
    >
      {text}
    </span>
  )
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={css({
        position: 'relative',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        overflow: 'hidden',
        width: ILLUSTRATION_WIDTH,
        height: ILLUSTRATION_HEIGHT,
        borderRadius: ILLUSTRATION_RADIUS,
        backgroundImage: `linear-gradient(158deg, ${palette.mockWashStart}, ${palette.mockWashEnd})`,
        boxShadow: 'inset 0 0 0 1px rgba(20, 28, 38, 0.05)',
      })}
    >
      <span
        className={css({
          position: 'absolute',
          top: BADGE_OFFSET,
          right: BADGE_OFFSET,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: BADGE_SIZE,
          height: BADGE_SIZE,
          borderRadius: '50%',
          backgroundColor: palette.phoneBezel,
          color: palette.white,
        })}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          width={BADGE_ICON_SIZE}
          height={BADGE_ICON_SIZE}
        >
          <path d="M18 6 6 18" />
          <path d="M6 6l12 12" />
        </svg>
      </span>
      <span
        className={css({
          display: 'block',
          width: CARD_WIDTH,
          paddingTop: '8px',
          paddingLeft: '9px',
          paddingRight: '9px',
          borderTopLeftRadius: '9px',
          borderTopRightRadius: '9px',
          backgroundColor: palette.phoneSurface,
          filter: 'grayscale(1)',
          opacity: 0.55,
        })}
      >
        <span
          className={css({
            display: 'block',
            marginBottom: '5px',
            fontSize: '6px',
            fontWeight: 700,
            lineHeight: '1',
            letterSpacing: '0.3px',
            color: palette.phoneTextMuted,
          })}
        >
          {t('illustration.brand')}
        </span>
        <span
          className={css({
            display: 'block',
            fontSize: '9px',
            fontWeight: 900,
            lineHeight: '1.1',
            color: palette.white,
          })}
        >
          {t('illustration.title')}
        </span>
        <span
          className={css({
            display: 'block',
            marginTop: '2px',
            marginBottom: '7px',
            fontSize: '6px',
            lineHeight: '1.2',
            color: palette.phoneTextSubtle,
          })}
        >
          {t('illustration.sub')}
        </span>
        <span
          className={css({
            display: 'flex',
            alignItems: 'center',
            marginBottom: '3px',
          })}
        >
          {[
            dot('a', true),
            line('l1'),
            dot('b', false),
            line('l2'),
            dot('c', false),
          ]}
        </span>
        <span
          className={css({
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: '7px',
          })}
        >
          {[
            t('illustration.placed'),
            t('illustration.ready'),
            t('illustration.delivered'),
          ].map(label)}
        </span>
      </span>
    </span>
  )
}

export default Illustration
